'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { Children, isValidElement, type ReactNode, useCallback, useEffect, useState } from 'react';

interface ContentCarouselProps {
	children: ReactNode[];
}

export function ContentCarousel({ children }: ContentCarouselProps) {
	const slides = Children.toArray(children);
	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'start',
		dragFree: true,
		loop: false,
		skipSnaps: false,
		slidesToScroll: 'auto',
		containScroll: 'trimSnaps',
		watchDrag: true,
		watchResize: true,
		watchSlides: true,
		duration: 25,
	});

	const [canScrollPrev, setCanScrollPrev] = useState(false);
	const [canScrollNext, setCanScrollNext] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const [totalPages, setTotalPages] = useState(1);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setCanScrollPrev(emblaApi.canScrollPrev());
		setCanScrollNext(emblaApi.canScrollNext());

		const snapCount = emblaApi.scrollSnapList().length;
		const selectedSnap = emblaApi.selectedScrollSnap();
		setTotalPages(snapCount);
		setActiveIndex(selectedSnap);
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		onSelect();
		emblaApi.on('select', onSelect);
		emblaApi.on('reInit', onSelect);
		return () => {
			emblaApi.off('select', onSelect);
			emblaApi.off('reInit', onSelect);
		};
	}, [emblaApi, onSelect]);

	if (slides.length === 0) return null;

	return (
		<div className="relative">
			{/* Page indicator — top right, above carousel */}
			{totalPages > 1 && (
				<div className="pointer-events-none absolute -top-12 right-0 z-10 flex items-center gap-4 md:-top-14">
					<div className="pointer-events-auto flex items-center gap-2 text-sm text-neutral-500">
						<button
							type="button"
							onClick={scrollPrev}
							disabled={!canScrollPrev}
							className="transition-colors hover:text-neutral-900 disabled:opacity-30"
							aria-label="Previous items"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
								fill="currentColor"
								className="h-4 w-4"
								aria-hidden="true"
							>
								<title>Previous</title>
								<path
									fillRule="evenodd"
									d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
									clipRule="evenodd"
								/>
							</svg>
						</button>
						<span>
							{activeIndex + 1} / {totalPages}
						</span>
						<button
							type="button"
							onClick={scrollNext}
							disabled={!canScrollNext}
							className="transition-colors hover:text-neutral-900 disabled:opacity-30"
							aria-label="Next items"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
								fill="currentColor"
								className="h-4 w-4"
								aria-hidden="true"
							>
								<title>Next</title>
								<path
									fillRule="evenodd"
									d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
									clipRule="evenodd"
								/>
							</svg>
						</button>
					</div>
				</div>
			)}

			{/* Carousel viewport */}
			<div ref={emblaRef} className="overflow-hidden cursor-grab active:cursor-grabbing">
				<div className="-ml-3 flex md:-ml-4">
					{slides.map((child) => {
						const slideKey =
							isValidElement(child) && child.key != null
								? child.key.toString()
								: `slide-${typeof child}-${String(child)}`;

						return (
							<div
								key={slideKey}
								className="min-w-0 shrink-0 basis-[85%] pl-3 sm:basis-[60%] md:basis-1/2 md:pl-4 lg:basis-1/3"
							>
								{child}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
